import React, { useState, useEffect } from 'react';
import MetaTags from 'react-meta-tags';
import { Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Dropzone from "react-dropzone";
import { BiTrash } from "react-icons/bi";
import excelimage from "../../assets/images/excel.png";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import Select from "react-select";
import _ from "lodash";
import { useLazyGetCountriesByCustomerQuery } from "../../store/country/country.api";
import { useLazyGetOperatorsByCountryAndCustomerQuery } from "../../store/operator/operator.api";
import { useGetCustomersQuery, useLazyGetSmscReceiversQuery } from "../../store/customer/customer.api";
import { _CONVERT_SMSC_FILE } from "../../helpers/url_helper";

const SmsConverter = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [receiver, setReceiver] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isGenerated, setIsGenerated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [countryOptions, setCountryOptions] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [receiverOptions, setReceiverOptions] = useState([]);

  // Fetch APIs
  const { data: customersData } = useGetCustomersQuery();
  const [fetchCountries, { data: countriesData }] = useLazyGetCountriesByCustomerQuery();
  const [fetchOperators, { data: operatorsData }] = useLazyGetOperatorsByCountryAndCustomerQuery();
  const [fetchReceivers, { data: receiversData }] = useLazyGetSmscReceiversQuery();

  // Handle Customer selection and fetch related Countries
  useEffect(() => {
    if (selectedCustomer) {
      fetchCountries(selectedCustomer.value);
    } else {
      setCountryOptions([]);
      setSelectedCountry(null);
    }
  }, [selectedCustomer, fetchCountries]);

  // Populate Countries based on Customer selection
  useEffect(() => {
    if (countriesData) {
      const data = Array.isArray(countriesData) ? countriesData : (_.get(countriesData, 'data') || _.get(countriesData, 'countries') || _.get(countriesData, 'message') || []);
      setCountryOptions(data.map(c => ({
        value: c.id || c.value,
        label: c.name || c.country || c.label
      })));
    }
  }, [countriesData]);

  // Populate Customers
  useEffect(() => {
    if (customersData) {
      const data = Array.isArray(customersData) ? customersData : (_.get(customersData, 'data') || _.get(customersData, 'customers') || _.get(customersData, 'message') || []);
      setCustomerOptions(data.map(c => ({
        value: c.id?.toString() || c.value,
        label: c.name || c.label
      })));
    }
  }, [customersData]);

  // Handle Country and Customer selection and fetch related Operators
  useEffect(() => {
    if (selectedCountry && selectedCustomer) {
      fetchOperators({ country_id: selectedCountry.value, customer_id: selectedCustomer.value });
    } else {
      setOperatorOptions([]);
      setSelectedOperator(null);
    }
  }, [selectedCountry, selectedCustomer, fetchOperators]);

  // Populate Operators based on Country and Customer selection
  useEffect(() => {
    if (operatorsData) {
      const data = Array.isArray(operatorsData) ? operatorsData : (_.get(operatorsData, 'data') || _.get(operatorsData, 'operators') || _.get(operatorsData, 'message') || []);
      setOperatorOptions(data.map(o => ({
        value: o.id?.toString() || o.value,
        label: o.operator_name ? `${o.operator_name} - ${o.country}` : (o.label || o.name)
      })));
    }
  }, [operatorsData]);

  // Handle Operator and Customer selection and fetch Receivers
  useEffect(() => {
    if (selectedOperator && selectedCustomer) {
      fetchReceivers({ operator_id: selectedOperator.value, customer_id: selectedCustomer.value });
    } else {
      setReceiverOptions([]);
      setReceiver(null);
    }
  }, [selectedOperator, selectedCustomer, fetchReceivers]);

  // Populate Receivers
  useEffect(() => {
    if (receiversData && receiversData.contacts) {
      setReceiverOptions(receiversData.contacts.map(contact => ({
        value: contact.phone,
        label: `${contact.phone} (${contact.location || 'Unknown'})`
      })));
    } else if (receiversData) {
      const data = Array.isArray(receiversData) ? receiversData : (_.get(receiversData, 'data') || _.get(receiversData, 'contacts') || []);
      setReceiverOptions(data.map(contact => ({
        value: contact.phone || contact.value,
        label: contact.label ? contact.label : (contact.phone ? `${contact.phone} (${contact.location || 'Unknown'})` : 'Unknown')
      })));
    }
  }, [receiversData]);

  const handleAcceptedFiles = (files) => {
    if (files.length > 0) {
      const file = files[0];
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      });
      setSelectedFile(file);
      setIsGenerated(false); // reset generation state on new file upload
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setIsGenerated(false);
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Validation: true if all required fields are filled
  const isFormValid = selectedFile && receiver && selectedCountry && selectedOperator && selectedCustomer;

  const handleGenerate = async () => {
    if (!selectedFile || !receiver || !selectedCountry || !selectedOperator || !selectedCustomer) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all required fields.",
      });
      return;
    }

    // Clean the receiver value by removing + and any spaces or non-digit characters
    const receiverValue = receiver.value.replace(/\D/g, '');
    if (!/^\d+$/.test(receiverValue) || receiverValue === '') {
      Swal.fire({
        icon: "warning",
        title: "Invalid Receiver",
        text: "Receiver number must contain only digits. Do not include '+' or spaces.",
      });
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("receiver", receiverValue);
    // Send the display names (labels) for the country, operator, and customer matching Python script expectations
    formData.append("country", selectedCountry.label);
    
    // For operator, label might include country name (e.g., 'Unitel - Laos'). The Python script expects just the operator name.
    // So we extract just the operator name.
    const operatorName = selectedOperator.label.split(" - ")[0];
    formData.append("operator", operatorName);
    formData.append("customer", selectedCustomer.label);

    try {
      const authUser = JSON.parse(localStorage.getItem("authUser") || '""');
      const response = await fetch(_CONVERT_SMSC_FILE, {
        method: "POST",
        headers: {
          auth: authUser,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMsg = "Failed to convert file";
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorMsg);
      }

      // Handle file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Construct descriptive filename: sms_history_Country_Customer_Operator_YYYY-MM-DD.xlsx
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const safeCountry = selectedCountry.label.replace(/[\s/\\:]+/g, '_');
      const safeCustomer = selectedCustomer.label.replace(/[\s/\\:]+/g, '_');
      const safeOperator = operatorName.replace(/[\s/\\:]+/g, '_');
      
      const smscDateHeader = response.headers.get('X-SMSC-Date');
      const dateString = smscDateHeader || formattedDate;
      
      const fileName = `sms_history_${safeCountry}_${safeCustomer}_${safeOperator}_${dateString}.xlsx`;
      
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const dateWarningHeader = response.headers.get('X-Date-Warning');
      const dateWarningType = response.headers.get('X-Date-Warning-Type');
      const dateWarningDetails = response.headers.get('X-Date-Warning-Details');

      if (dateWarningHeader === 'true') {
        let warningText = "The file was converted and downloaded, but contains date issues.";
        if (dateWarningType === 'DIFFERENT_DATES') {
          warningText = `The file contains multiple different dates (${dateWarningDetails}). ENPT reports require a single test date.`;
        } else if (dateWarningType === 'UNSUPPORTED_DATE') {
          warningText = `The file contains unsupported date formats (e.g. '${dateWarningDetails}').`;
        } else if (dateWarningType === 'UNSUPPORTED_TZ') {
          warningText = `The file contains unsupported timezone formats (e.g. '${dateWarningDetails}').`;
        }

        Swal.fire({
          icon: "warning",
          title: "Date Warning Detected",
          text: warningText,
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "File converted and downloaded successfully!",
        });
      }
      
      // Clear all fields after download
      setSelectedFile(null);
      setReceiver(null);
      setSelectedCountry(null);
      setSelectedOperator(null);
      setSelectedCustomer(null);
      setIsGenerated(false);
      
    } catch (error) {
      console.error("Conversion error:", error);
      Swal.fire({
        icon: "error",
        title: "Generation Failed",
        text: error.message || "An error occurred during conversion",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    Swal.fire({
      icon: "info",
      title: "Downloading...",
      text: "Simulating file download.",
      timer: 1500,
      showConfirmButton: false
    });
  };

  return (
    <React.Fragment>
      <MetaTags>
        <title>SMS HISTORY GENERATOR </title>
      </MetaTags>

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="ENPT" breadcrumbItem="SMS HiSTORY GENERATOR" />

          <Row>
            <Col className="col-12">
              <Card className="bg-white">
                <CardBody>
                  <div style={{ minHeight: "400px" }}>
                    <Row>
                      <Col md="6">
                        <h5 className="mb-4">Upload SMSC Inspector File</h5>
                        <Form>
                          <Dropzone
                            className="dropzone1"
                            maxFiles={1}
                            multiple={false}
                            validator={file => {
                              var x = file.path?.split(".")
                              let extension = x[x.length - 1]
                              if (extension === "xlsx" || extension === "xls" || extension === "csv") {
                                return null
                              } else {
                                Swal.fire({
                                  icon: "error",
                                  title: "Oops...",
                                  text: "Please select an XLSX or CSV file.",
                                })
                                return {
                                  error: "Please select an XLSX or CSV file.",
                                }
                              }
                            }}
                            onDrop={acceptedFiles => {
                              if (acceptedFiles.length !== 0) {
                                handleAcceptedFiles(acceptedFiles)
                              }
                            }}
                          >
                            {({ getRootProps, getInputProps }) => (
                              <div className="dropzone1 mb-3">
                                <div
                                  className="dz-message needsclick"
                                  style={{ marginTop: "5px" }}
                                  {...getRootProps()}
                                >
                                  <input {...getInputProps()} />
                                  <div className="mb-1 box3">
                                    <i className="upload_icon fas fa-cloud-upload-alt"></i>
                                  </div>
                                  <h4>
                                    Drop file here or{" "}
                                    <span className="upload_text">
                                      click to upload{" "}
                                    </span>
                                  </h4>
                                </div>
                              </div>
                            )}
                          </Dropzone>
                        </Form>

                        <div className="dropzone-previews" id="file-previews">
                          {!selectedFile ? (
                            <Row>
                              <div>
                                <h6 className="file_text">
                                  <i
                                    className=" bx bx-paperclip "
                                    style={{ rotate: "140deg" }}
                                  />
                                  &nbsp; No file added
                                </h6>
                              </div>
                              <div className="support_text"> Supports: XLSX, CSV</div>
                              <div className="support_text"> Maximum file size of 20mb</div>
                            </Row>
                          ) : (
                            <Card
                              style={{ boxShadow: "none" }}
                              className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                            >
                              <div className="">
                                <Row className="align-items-center">
                                  <Col className="col-auto">
                                    <img
                                      data-dz-thumbnail=""
                                      height="68"
                                      className="rounded bg-light"
                                      src={excelimage}
                                      alt="Excel preview"
                                    />
                                  </Col>
                                  <Col>
                                    <Link
                                      to="#"
                                      className="text-muted font-weight-bold truncate-text"
                                    >
                                      {selectedFile.name.length > 45 
                                        ? `${selectedFile.name.substring(0, 21)}...${selectedFile.name.substring(selectedFile.name.length - 21)}` 
                                        : selectedFile.name}
                                    </Link>
                                    <p className="mb-0">
                                      <strong>{selectedFile.formattedSize}</strong>
                                    </p>
                                  </Col>
                                  <Col className="col-auto">
                                    <Button
                                      className="me-2"
                                      color="danger"
                                      size="sm"
                                      onClick={removeFile}
                                    >
                                      <BiTrash />
                                    </Button>
                                  </Col>
                                </Row>
                              </div>
                            </Card>
                          )}
                        </div>
                      </Col>

                      <Col md="6">
                        <h5 className="mb-4">Configuration</h5>
                        <FormGroup className="mb-4">
                          <Label for="customer">Customer</Label>
                          <Select
                            name="customer"
                            isSearchable
                            isClearable
                            placeholder="Select Customer"
                            options={customerOptions}
                            value={selectedCustomer}
                            onChange={setSelectedCustomer}
                          />
                        </FormGroup>
                        <FormGroup className="mb-3">
                          <Label for="country">Country</Label>
                          <Select
                            name="country"
                            isSearchable
                            isClearable
                            placeholder="Select Country"
                            options={countryOptions}
                            value={selectedCountry}
                            onChange={setSelectedCountry}
                            isDisabled={!selectedCustomer}
                          />
                        </FormGroup>
                        <FormGroup className="mb-3">
                          <Label for="operator">Operator</Label>
                          <Select
                            name="operator"
                            isSearchable
                            isClearable
                            placeholder="Select Operator"
                            options={operatorOptions}
                            value={selectedOperator}
                            onChange={setSelectedOperator}
                            isDisabled={!selectedCountry || !selectedCustomer}
                          />
                        </FormGroup>
                        <FormGroup className="mb-3">
                          <Label for="receiver">Receiver</Label>
                          <Select
                            name="receiver"
                            isSearchable
                            isClearable
                            placeholder="Select Receiver"
                            options={receiverOptions}
                            value={receiver}
                            onChange={setReceiver}
                            isDisabled={!selectedOperator || !selectedCustomer}
                          />
                        </FormGroup>
                        
                        <div className="d-flex justify-content-end">
                          <Button
                            color={isGenerated ? "success" : "danger"}
                            className="btn btn-custom w-100"
                            onClick={handleGenerate}
                            disabled={!isFormValid || isLoading}
                            style={{ height: '40px' }}
                          >
                            {isLoading ? (
                              <i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                            ) : isGenerated ? (
                              <i className="bx bx-download font-size-16 align-middle me-2"></i>
                            ) : null}
                            {isLoading ? "Generating..." : isGenerated ? "Save / Download Excel" : "Generate"}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SmsConverter;
